import React, { useCallback, useMemo, useRef } from 'react';
import type { AnalysisProps } from '../../types';
import type { Analysis } from 'venn-api';
import { useRecoilValue } from 'recoil';
import {
  blockDisplayHeader,
  blockFonts,
  blockLimitedRequestSubjects,
  flattenedProxyMapState,
  getCustomFontSize,
  studioPrintOrientationType,
  subjectAsOfDate,
} from 'venn-state';
import { compact, isEqual } from 'lodash';
import { MAX_INVESTMENTS } from '../../../charts/analysis-charts/correlation-analysis/logic';
import { portfolioCorrelationExtractor } from '../../../studio-blocks/components/charts/pairwise-correlation/dataExtractors';
import { assertNotNil, Dates, type ExcelCell, plural } from 'venn-utils';
import type { TabularDataRow } from '../../../studio-blocks/components/charts/pairwise-correlation/types';
import useExportUpdate from '../../../studio-blocks/logic/useExportUpdate';
import { EllipsisTooltipSpan, Hint } from 'venn-ui-kit';
import { type BasicTableColumn, ColumnAlign } from '../../../basictable';
import VerticalHeader from '../../../studio-blocks/components/charts/pairwise-correlation/VerticalHeader';
import { pairwiseCorrelationCellRenderer } from '../../../studio-blocks/components/charts/pairwise-correlation/cellRenderers';
import CorrelationColorLegend from '../../../studio-blocks/components/charts/pairwise-correlation/CorrelationColorLegend';
import {
  CorrelationGrid,
  FIRST_COLUMN_INNER_WIDTH,
  FIRST_COLUMN_OUTER_WIDTH_PX,
} from '../../components/visualizations/grids/correlation-grid';
import { usePageProxySymbol } from '../../components/core/block-subject-footnotes-and-superscripts';
import { toTypeUnsafeSubject } from '../../../studio-blocks/logic/exportUtils';
import { useIsCorrelationLabelFull } from '../../../studio-blocks/logic/useCorrelationLabelDisplay';
import { OptionIcon } from '../../../common/OptionIcon';

type PortfolioCorrelationProps = Omit<AnalysisProps, 'analyses'> & {
  analysis: Analysis | undefined;
};

export const PortfolioCorrelation = ({ id, requests, analysis, exportMetaData }: PortfolioCorrelationProps) => {
  const proxySymbol = usePageProxySymbol();
  const tableFont = useRecoilValue(blockFonts.blockTableData(id));
  const legendFont = useRecoilValue(blockFonts.blockChartLegend(id));
  const proxyMap = useRecoilValue(flattenedProxyMapState);
  const printOrientation = useRecoilValue(studioPrintOrientationType);
  const header = useRecoilValue(blockDisplayHeader(id));
  const isFullLabelDisplay = useIsCorrelationLabelFull();
  const correlations =
    analysis?.analysisType === 'CORRELATION'
      ? compact(analysis?.correlationMatrix?.correlations)
      : compact(analysis?.downsideCorrelationMatrix?.correlations); // only display first portfolio
  const hasBenchmark = requests[0]!.benchmark !== undefined;
  const numberExcluded = correlations.length - MAX_INVESTMENTS;

  const subject = useRecoilValue(blockLimitedRequestSubjects(id))[0];
  const asOfDate = useRecoilValue(subjectAsOfDate([id, subject ? toTypeUnsafeSubject(subject) : undefined]));

  const data = useMemo(
    () => portfolioCorrelationExtractor(correlations, requests[0]!.subject.portfolio, hasBenchmark),
    [correlations, requests, hasBenchmark],
  );

  const dataRef = useRef(data);

  if (!isEqual(dataRef.current, data)) {
    dataRef.current = data;
  }

  const excelDataFn = useCallback(() => {
    const isRelative = requests[0]?.relative;
    const benchmarkName = requests[0]!.benchmark?.name ?? '--';
    const headerRow: ExcelCell[] = dataRef.current.map((data: TabularDataRow) => ({
      value: isRelative ? `${data.label} (Relative to ${benchmarkName})` : data.label,
      bold: true,
    }));
    const body: ExcelCell[][] = dataRef.current.map((row: TabularDataRow) => {
      const cellData: ExcelCell[] = [];
      for (let subjectIdx = 0; subjectIdx < correlations.length; subjectIdx++) {
        cellData.push({
          value: row[subjectIdx]?.value ?? '--',
          percentage: row?.type === 'percentage',
          digits: correlations.length,
        });
      }
      return [{ value: row.label, bold: true }, ...cellData];
    });
    return compact([
      subject && asOfDate && [{ value: `${subject.name} (As of ${Dates.toDayMonthYear(asOfDate)})`, bold: true }],
      [{ value: header, bold: true }, ...headerRow],
      ...body,
    ]);
  }, [requests, header, correlations.length, subject, asOfDate]);

  useExportUpdate({ selectedRefId: id, exportMetaData, excelDataFn });

  const correlationCellRenderer = useCallback(
    (rowData: TabularDataRow) => {
      const labelContent = [rowData?.fundId && proxyMap?.[rowData.fundId] && <sup>{proxySymbol}</sup>, rowData.label];

      const icon = rowData.optionData ? <OptionIcon optionType={rowData.optionData.optionType} /> : null;

      return isFullLabelDisplay ? (
        <span>
          {icon} {labelContent}
        </span>
      ) : (
        <EllipsisTooltipSpan usePortal maxWidth={FIRST_COLUMN_INNER_WIDTH}>
          {icon} {labelContent}
        </EllipsisTooltipSpan>
      );
    },
    [proxyMap, proxySymbol, isFullLabelDisplay],
  );

  const getColumns = (): BasicTableColumn<unknown>[] =>
    compact([
      {
        label: 'Correlation',
        accessor: 'label',
        headerStyle: {
          maxWidth: FIRST_COLUMN_OUTER_WIDTH_PX,
          fontSize: `${tableFont.fontSizePt}pt`,
        },
        cellRenderer: correlationCellRenderer,
      },
      ...data.map((row, colNum) => {
        const rowHeaderRenderer = (_: unknown, data: TabularDataRow[]) => {
          const rowData = assertNotNil(data[colNum]);
          const icon = rowData.optionData ? <OptionIcon optionType={rowData.optionData.optionType} /> : null;
          return (
            <VerticalHeader>
              {icon} {rowData.label}
            </VerticalHeader>
          );
        };
        return {
          label: row.label,
          align: ColumnAlign.RIGHT,
          cellRenderer: pairwiseCorrelationCellRenderer(colNum, 0, undefined),
          headerRenderer: rowHeaderRenderer,
        };
      }),
    ]);

  return (
    <>
      <CorrelationGrid
        columns={getColumns()}
        data={data}
        compactRows={printOrientation === 'LANDSCAPE'}
        fontSize={getCustomFontSize(tableFont)}
        isFullSizeLabels={isFullLabelDisplay}
      />
      <CorrelationColorLegend fontSize={getCustomFontSize(legendFont)} />
      {numberExcluded > 0 ? (
        <div className="flex flex-column justify-center px-[9px] pt-[9px]">
          <Hint>
            {plural(numberExcluded, {
              1: '{{count}} smaller investment has been excluded.',
              other: '{{count}} smaller investments have been excluded.',
            })}
          </Hint>
        </div>
      ) : null}
    </>
  );
};
